import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Plus, Heart } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { track } from "@/lib/analytics";
import { supabase, type JewelryItem } from "@/lib/supabase";
import { useProfile } from "@/lib/useProfile";
import { AppShell } from "@/components/app-shell";
import { ImageBox } from "@/components/ui-extras";
import { bgColorFor } from "@/components/bg-preference-selector";
import { EmptyState } from "@/components/empty-state";
import { HintBanner } from "@/components/hint-banner";
import { Gem } from "lucide-react";
import { PhotoUpload } from "@/components/photo-upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { FavoriteHeart, ItemActions } from "@/components/item-actions";
import { useCardReveal, CardRevealOverlay } from "@/components/card-reveal";
import { JewelryEditDialog } from "@/components/jewelry-edit-dialog";
import { QuickAddSheet } from "@/components/quick-add-sheet";


import { CustomizeViewDialog, JEWELRY_FIELDS } from "@/components/customize-view-dialog";
import { NearEmptyBanner } from "@/components/collection-prompts";
import { KeyboardAwareFormBody } from "@/components/keyboard-aware-form-body";
import { useItemLimits, FREE_LIMITS } from "@/lib/useSubscription";
import { UpgradePromptSheet } from "@/components/upgrade-prompt-sheet";
import {
  SmartSelect,
  JEWELRY_CATEGORY_PRESETS,
  JEWELRY_COLOR_PRESETS,
  JEWELRY_MATERIAL_PRESETS,
  JEWELRY_STORAGE_PRESETS,
  SHARED_OCCASION_PRESETS,
} from "@/components/smart-select";
import {
  CollectionToolbar,
  applyTextSearch,
  type ActiveFilters,
  type FilterFieldDef,
  type SortOption,
} from "@/components/collection-toolbar";
import { useMemo } from "react";
import { useOwnedJewelry } from "@/lib/useCollection";
import { useClosetCollection } from "@/hooks/useClosetCollection";

export const Route = createFileRoute("/jewelry/")({
  validateSearch: (s: Record<string, unknown>): { new?: number; sort?: string } => ({
    new: s.new === 1 || s.new === "1" ? 1 : undefined,
    sort: typeof s.sort === "string" ? s.sort : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Jewelry — Cailève" },
      { name: "description", content: "Your curated jewelry collection." },
    ],
  }),
  component: () => (
    <AppShell>
      <JewelryPage />
    </AppShell>
  ),
});

function JewelryPage() {
  const { user } = useAuth();
  if (!user) return null;
  const uid = user.id;
  const [open, setOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const limits = useItemLimits();
  const jewelryUpgradeReason: "item_limit" | "item_limit_lapsed" =
    (limits.counts?.jewelry ?? 0) > FREE_LIMITS.jewelry
      ? "item_limit_lapsed"
      : "item_limit";
  const tryOpenAdd = () => {
    if (limits.canAddJewelry) setOpen(true);
    else setUpgradeOpen(true);
  };
  const search = Route.useSearch();
  useEffect(() => {
    if (search.new === 1) tryOpenAdd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.new, limits.canAddJewelry]);
  const qc = useQueryClient();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<ActiveFilters>(
    () => (search.sort === "favorites" ? { is_favorite: true } : {})
  );
  const [sortValue, setSortValue] = useState<string>(search.sort ?? "recent");
  const { data: profile } = useProfile();
  const visibleFields = profile?.jewelry_card_fields ?? ["category"];

  const { data: crossCount } = useQuery({
    queryKey: ["jewelry_cross_count", uid],
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { count } = await supabase
        .from("clothing_items")
        .select("id", { count: "exact", head: true })
        .eq("user_id", uid);
      const { count: a } = await supabase
        .from("jewelry_items")
        .select("id", { count: "exact", head: true })
        .eq("user_id", uid)
        .eq("is_accessory", true);
      return (count ?? 0) + (a ?? 0);
    },
  });

  const { data, isLoading } = useOwnedJewelry();

  // Background prefetch — warms the cache for swipe navigation on clothing/accessory detail screens
  useQuery({
    queryKey: ["clothing_items", uid],
    enabled: !!uid,
    staleTime: 2 * 60 * 1000,
    throwOnError: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clothing_items")
        .select("id, name, photo_url, clothing_type, primary_color, occasion, is_favorite, favorited_at, is_wishlist, created_at")
        .eq("user_id", uid!)
        .eq("is_wishlist", false)
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message || "Network error");
      return data ?? [];
    },
  });

  useQuery({
    queryKey: ["accessory_items", uid],
    enabled: !!uid,
    staleTime: 2 * 60 * 1000,
    throwOnError: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("jewelry_items")
        .select("id, name, photo_url, category, accessory_type, is_favorite, favorited_at, is_accessory, is_wishlist, created_at, eye_verdict")
        .eq("user_id", uid!)
        .eq("is_accessory", true)
        .eq("is_wishlist", false)
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message || "Network error");
      return data ?? [];
    },
  });

  const {
    wishlistItems,
    toggleFavorite: toggleFav,
    deleteItem: del,
    promoteFromWishlist,
  } = useClosetCollection({ type: "jewelry", uid });


  const deletingItem = data?.find((j) => j.id === deleteId) ?? null;
  const editingItem = data?.find((j) => j.id === editId) ?? null;

  const formatFieldValue = (j: JewelryItem, key: string): string | null => {
    switch (key) {
      case "category": return j.category;
      case "color": return j.color;
      case "material": return j.material;
      case "storage_location": return j.storage_location;
      case "cost": return j.cost != null ? `$${j.cost}` : null;
      case "notes": return j.notes;
      default: return null;
    }
  };

  const unique = (key: keyof JewelryItem): string[] =>
    Array.from(
      new Set(
        (data ?? [])
          .map((j) => j[key])
          .filter((v): v is string => typeof v === "string" && v.length > 0),
      ),
    ).sort();

  const filterFields: FilterFieldDef[] = useMemo(
    () => [
      { key: "category", label: "Category", type: "select", options: unique("category") },
      { key: "color", label: "Color", type: "select", options: unique("color") },
      { key: "material", label: "Material", type: "select", options: unique("material") },
      { key: "storage_location", label: "Storage", type: "select", options: unique("storage_location") },
      { key: "occasion", label: "Occasion", type: "select", options: unique("occasion") },
      { key: "is_favorite", label: "Favorites only", type: "toggle" },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  const sortOptions: SortOption[] = [
    { value: "recent", label: "Recently Added" },
    { value: "name_asc", label: "Name A → Z" },
    { value: "name_desc", label: "Name Z → A" },
    { value: "category", label: "Category" },
    { value: "color", label: "Color" },
    { value: "material", label: "Material" },
    { value: "price_asc", label: "Price Low → High" },
    { value: "price_desc", label: "Price High → Low" },
    { value: "favorites", label: "Favorites First" },
  ];

  const displayItems = useMemo(() => {
    let items = data ?? [];
    if (filters.is_favorite) items = items.filter((j) => j.is_favorite);
    for (const k of ["category", "color", "material", "storage_location", "occasion"] as const) {
      const v = filters[k];
      if (typeof v === "string" && v) items = items.filter((j) => j[k] === v);
    }
    items = applyTextSearch(items, searchQuery, [
      "name",
      "category",
      "color",
      "material",
      "storage_location",
      "notes",
      "occasion",
    ]);
    const sorted = [...items];
    const cmp = (a?: string | null, b?: string | null) =>
      (a ?? "").localeCompare(b ?? "");
    switch (sortValue) {
      case "name_asc": sorted.sort((a, b) => cmp(a.name, b.name)); break;
      case "name_desc": sorted.sort((a, b) => cmp(b.name, a.name)); break;
      case "category": sorted.sort((a, b) => cmp(a.category?.toLowerCase().trim(), b.category?.toLowerCase().trim())); break;
      case "color": sorted.sort((a, b) => cmp(a.color?.toLowerCase().trim(), b.color?.toLowerCase().trim())); break;
      case "material": sorted.sort((a, b) => cmp(a.material?.toLowerCase().trim(), b.material?.toLowerCase().trim())); break;
      case "price_asc": sorted.sort((a, b) => (a.cost ?? Infinity) - (b.cost ?? Infinity)); break;
      case "price_desc": sorted.sort((a, b) => (b.cost ?? -Infinity) - (a.cost ?? -Infinity)); break;
      case "favorites": sorted.sort((a, b) => Number(!!b.is_favorite) - Number(!!a.is_favorite)); break;
      default: sorted.sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
    }
    return sorted;
  }, [data, filters, searchQuery, sortValue]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex items-end justify-between border-b border-border pb-6 mb-6">
        <div>
          <h1 className="font-serif-display text-4xl">Your Jewelry</h1>
          <p className="font-serif-display italic text-base mt-1" style={{ color: "var(--caileve-lilac)" }}>The pieces Cailève styles from first.</p>
          {data && (
            <p style={{ fontFamily: "Inter, sans-serif", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", color: "var(--caileve-muted)", marginTop: 6 }}>
              {(searchQuery || Object.values(filters).some((v) => v !== undefined && v !== "" && v !== false))
                ? `${displayItems.length} of ${data.length} pieces`
                : `${data.length} ${data.length === 1 ? "piece" : "pieces"}`}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <CustomizeViewDialog
            userId={uid}
            column="jewelry_card_fields"
            options={JEWELRY_FIELDS}
            current={visibleFields}
            title="Customize Jewelry Cards"
          />
          <Button
            type="button"
            onClick={tryOpenAdd}
            className="bg-[var(--caileve-gold)] text-[var(--caileve-bg)] hover:bg-[var(--caileve-gold)]/90"
            style={{ borderRadius: "var(--caileve-radius-pill)" }}
          >
            <Plus className="h-4 w-4 mr-2" /> Add Piece
          </Button>
          <QuickAddSheet
            open={open}
            onOpenChange={setOpen}
            userId={uid}
            defaultType="jewelry"
            onDone={() => qc.invalidateQueries({ queryKey: ["jewelry_items", uid] })}
          />
          <UpgradePromptSheet open={upgradeOpen} onOpenChange={setUpgradeOpen} reason={jewelryUpgradeReason} />
        </div>
      </div>
      {/* Collection type switcher */}
      <div className="flex gap-2 mb-6">
        {([
          { to: "/jewelry" as const, label: "Jewelry", search: { new: undefined } as { new: number | undefined } },
          { to: "/clothing" as const, label: "Clothing", search: { new: undefined } as { new: number | undefined } },
          { to: "/accessories" as const, label: "Accessories", search: { new: undefined } as { new: number | undefined } },
        ] as const).map(({ to, label, search }) => {
          const pathname = window.location.pathname;
          const active = pathname === to || pathname.startsWith(to + "/");
          return (
            <Link
              key={to}
              to={to}
              search={search}
              className="px-4 text-sm transition-colors"
              style={
                active
                  ? {
                      backgroundColor: "transparent",
                      color: "var(--caileve-ink)",
                      borderBottom: "2px solid var(--caileve-gold)",
                      borderRadius: 0,
                      fontWeight: 600,
                      paddingBottom: 6,
                    }
                  : {
                      backgroundColor: "transparent",
                      color: "var(--caileve-muted)",
                      border: "none",
                      paddingBottom: 6,
                    }
              }
            >
              {label}
            </Link>
          );
        })}
      </div>
      {(wishlistItems?.length ?? 0) > 0 &&
        localStorage.getItem("caileve_considering_prompt_dismissed_v1") !== "1" && (
        <button
          type="button"
          onClick={() => {
            document.getElementById("considering-section")?.scrollIntoView({ behavior: "smooth" });
          }}
          className="flex items-center justify-between w-full px-4 py-3 mb-4 text-left transition-opacity hover:opacity-80"
          style={{
            backgroundColor: "var(--caileve-soft)",
            borderLeft: "3px solid var(--caileve-gold)",
          }}
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: "var(--caileve-muted)" }}>
              Your Edit
            </p>
            <p className="font-serif-display text-lg mt-0.5" style={{ color: "var(--caileve-ink)" }}>
              {wishlistItems!.length} {wishlistItems!.length === 1 ? "piece" : "pieces"} in Your Edit
            </p>
            <p
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: "italic",
                fontSize: 13,
                color: "var(--caileve-muted)",
                marginTop: 2,
              }}
            >
              Pieces you are still deciding on.
            </p>
          </div>
          <span className="text-[var(--caileve-muted)] text-sm">↓</span>
        </button>
      )}


      {isLoading ? (
        <CollectionSkeletonGrid count={(data as JewelryItem[] | undefined)?.length || 8} />
      ) : !data?.length ? (
        <NearEmptyBanner kind="jewelry" itemCount={0} onCta={tryOpenAdd} crossCount={crossCount ?? 0} />
      ) : (
        <>
          <HintBanner
            hintKey="hint_jewelry_3"
            show={(data?.length ?? 0) >= 3}
            message="Style This Piece is now active — tap any piece to see what it pairs with in your collection."
          />
          <CollectionToolbar
            searchPlaceholder="Search your jewelry…"
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            filterFields={filterFields}
            activeFilters={filters}
            onFilterChange={(k, v) => setFilters((p) => ({ ...p, [k]: v }))}
            onClearFilters={() => setFilters({})}
            sortOptions={sortOptions}
            sortValue={sortValue}
            onSortChange={setSortValue}
          />
          {displayItems.length === 0 ? (
            <div className="border border-dashed border-[var(--caileve-soft)] py-24 text-center">
              <p
                className="font-serif-display text-2xl"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-lilac)" }}
              >
                No pieces match your search.
              </p>
              <button
                type="button"
                onClick={() => { setSearchQuery(""); setFilters({}); }}
                className="mt-6 px-6 py-2 text-xs uppercase tracking-[0.25em] text-white"
                style={{ backgroundColor: "var(--caileve-lilac)" }}
              >
                Clear Filters
              </button>
            </div>
          ) : (
        <div className="collection-content grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {displayItems.map((j) => (
            <JewelryGridCard
              key={j.id}
              item={j}
              showFavorite={visibleFields.includes("is_favorite")}
              onToggleFav={() => toggleFav.mutate({ id: j.id, next: !j.is_favorite })}
              onEdit={() => setEditId(j.id)}
              onDelete={() => setDeleteId(j.id)}
            />
          ))}
        </div>
          )}
        </>
      )}

      {wishlistItems && wishlistItems.length > 0 && (
        <section id="considering-section" className="mt-16">
          <div
            style={{
              height: 1,
              backgroundColor: "var(--caileve-gold)",
              width: "100%",
              marginBottom: 32,
            }}
          />
          <h2
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: 24,
              color: "var(--caileve-muted)",
            }}
          >
            Your Edit
          </h2>
          <p
            style={{
              fontSize: 13,
              fontStyle: "italic",
              color: "var(--caileve-muted)",
              marginTop: 4,
              marginBottom: 24,
            }}
          >
            Pieces you're thinking about.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {wishlistItems.map((w) => (
              <div key={w.id}>
                <Link
                  to="/jewelry/$id"
                  params={{ id: w.id }}
                  className="block cursor-pointer"
                >
                  <ImageBox src={w.photo_url} alt={w.name} />
                  <div className="mt-3">
                    <p className="font-serif-display text-xl leading-tight line-clamp-2">
                      {w.name}
                    </p>
                  </div>
                </Link>
                <button
                  type="button"
                  onClick={() => promoteFromWishlist.mutate(w.id)}
                  disabled={promoteFromWishlist.isPending}
                  style={{
                    fontSize: 11,
                    color: "var(--caileve-lilac)",
                    textDecoration: "underline",
                    background: "none",
                    border: "none",
                    padding: "6px 0 0",
                    cursor: "pointer",
                  }}
                >
                  Add to my collection
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this piece?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingItem
                ? `"${deletingItem.name}" will be permanently removed from your jewelry collection.`
                : "This piece will be permanently removed."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={del.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={del.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (deleteId) {
                  del.mutate(deleteId, {
                    onSuccess: () => setDeleteId(null),
                  });
                }
              }}
            >
              {del.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editId} onOpenChange={(o) => !o && setEditId(null)}>
        {editingItem && (
          <JewelryEditDialog
            item={editingItem}
            userId={uid}
            onDone={() => setEditId(null)}
          />
        )}
      </Dialog>
    </div>
  );
}

const EMPTY_JEWELRY = {
  name: "",
  photo_url: "",
  category: "",
  color: "",
  material: "",
  storage_location: "",
  cost: "",
  occasion: "",
  notes: "",
  is_favorite: false,
};

function AddJewelryDialog({
  open,
  userId,
  onDone,
}: {
  open: boolean;
  userId: string;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [f, setF] = useState({ ...EMPTY_JEWELRY });
  // Reset every time the dialog opens so no stale data carries over.
  useEffect(() => {
    if (open) setF({ ...EMPTY_JEWELRY });
  }, [open]);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) =>
    setF((p) => ({ ...p, [k]: v }));

  // Single bulk fetch of all jewelry user_field_options — replaces the
  // per-SmartSelect N+1 fetches that previously fired one query per field.
  const JEWELRY_FIELD_NAMES = [
    "jewelry_category",
    "jewelry_color",
    "jewelry_material",
    "jewelry_storage",
    "jewelry_occasion",
  ] as const;
  const { data: jewelryFieldOptions } = useQuery({
    queryKey: ["user_field_options", userId, "__jewelry_bulk__"],
    enabled: open,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_field_options")
        .select("field_name, custom_value")
        .eq("user_id", userId)
        .in("field_name", JEWELRY_FIELD_NAMES as unknown as string[])
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message || "Network error");
      return (data ?? []) as { field_name: string; custom_value: string }[];
    },
  });
  const optionsFor = (field: string): string[] =>
    (jewelryFieldOptions ?? [])
      .filter((r) => r.field_name === field)
      .map((r) => r.custom_value);
  const categoryOptions = optionsFor("jewelry_category");
  const colorOptions = optionsFor("jewelry_color");
  const materialOptions = optionsFor("jewelry_material");
  const storageOptions = optionsFor("jewelry_storage");
  const occasionOptions = optionsFor("jewelry_occasion");

  const m = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("jewelry_items").insert({
        user_id: userId,
        name: f.name,
        photo_url: f.photo_url || null,
        category: f.category || null,
        color: f.color || null,
        material: f.material || null,
        storage_location: f.storage_location || null,
        cost: f.cost ? Number(f.cost) : null,
        occasion: f.occasion || null,
        notes: f.notes || null,
        is_favorite: f.is_favorite,
      } as never);
      if (error) throw new Error(error.message || "Network error");
    },
    onSuccess: () => {
      toast.success("Added to your collection", {
        description: "Every great collection begins with a single piece. Yours just did.",
      });
      track("item_added", { item_type: "jewelry" });
      qc.invalidateQueries({ queryKey: ["jewelry_items", userId] });
      qc.invalidateQueries({ queryKey: ["jewelry_favorites", userId] });
      // Reset so the user can add another piece immediately.
      setF({ ...EMPTY_JEWELRY });
      onDone();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <DialogContent className="max-w-lg max-h-[85vh] sm:max-h-[80vh] p-0 gap-0 flex flex-col overflow-hidden">
      <DialogHeader className="shrink-0 px-6 pt-6 pb-4 border-b border-border bg-background">
        <DialogTitle className="font-serif-display text-3xl">Add Jewelry</DialogTitle>
      </DialogHeader>
      <KeyboardAwareFormBody>
        <Field label="Name">
          <Input value={f.name} onChange={(e) => set("name", e.target.value)} maxLength={120} />
        </Field>
        <Field label="Photo">
          <p
            className="italic text-center mb-2"
            style={{ fontSize: 12, color: "var(--caileve-muted)", fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            Your photo is stored privately — only you can see it.
          </p>
          <PhotoUpload
            userId={userId}
            folder="jewelry"
            value={f.photo_url || null}
            onChange={(p) => set("photo_url", p ?? "")}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <SmartSelect userId={userId} fieldName="jewelry_category" presets={JEWELRY_CATEGORY_PRESETS} customOptions={categoryOptions} value={f.category} onChange={(v) => set("category", v)} />
          </Field>
          <Field label="Material">
            <SmartSelect userId={userId} fieldName="jewelry_material" presets={JEWELRY_MATERIAL_PRESETS} customOptions={materialOptions} value={f.material} onChange={(v) => set("material", v)} />
          </Field>
          <Field label="Color">
            <SmartSelect userId={userId} fieldName="jewelry_color" presets={JEWELRY_COLOR_PRESETS} customOptions={colorOptions} value={f.color} onChange={(v) => set("color", v)} />
          </Field>
          <Field label="Storage">
            <SmartSelect userId={userId} fieldName="jewelry_storage" presets={JEWELRY_STORAGE_PRESETS} customOptions={storageOptions} value={f.storage_location} onChange={(v) => set("storage_location", v)} />
          </Field>
        </div>
        <Field label="Cost">
          <Input
            type="number"
            value={f.cost}
            onChange={(e) => set("cost", e.target.value)}
          />
        </Field>
        <Field label="Occasion">
          <SmartSelect userId={userId} fieldName="jewelry_occasion" presets={SHARED_OCCASION_PRESETS} customOptions={occasionOptions} value={f.occasion} onChange={(v) => set("occasion", v)} />
        </Field>
        <Field label="Notes">
          <Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={3} />
        </Field>
        <button
          type="button"
          onClick={() => set("is_favorite", !f.is_favorite)}
          className={cn(
            "flex items-center gap-3 px-4 py-3 border transition-all duration-300 w-full",
            f.is_favorite
              ? "border-[var(--caileve-copper)] bg-[var(--caileve-copper)]/5"
              : "border-border hover:border-[var(--caileve-copper)]/40"
          )}
        >
          <Heart
            className={cn(
              "h-5 w-5 transition-all duration-300",
              f.is_favorite
                ? "fill-[var(--caileve-copper)] text-[var(--caileve-copper)]"
                : "text-muted-foreground"
            )}
          />
          <span
            className={cn(
              "text-sm tracking-wide",
              f.is_favorite ? "text-[var(--caileve-copper)]" : "text-muted-foreground"
            )}
          >
            {f.is_favorite ? "Favorited" : "Mark as favorite"}
          </span>
        </button>
      </KeyboardAwareFormBody>
      <DialogFooter className="shrink-0 px-6 py-4 border-t border-border bg-background">
        <Button
          onClick={() => {
            if (!f.name.trim()) return toast.error("Name is required");
            m.mutate();
          }}
          disabled={m.isPending}
          className="bg-[var(--caileve-lilac)] text-[var(--caileve-bg)] hover:bg-[var(--caileve-lilac)]/90"
        >
          {m.isPending ? "Saving…" : "Add to Collection"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

function compositionLine(item: { brand?: string | null; material?: string | null }, archetypePrimary: string | null): string | null {
  if (item.brand) return `${item.brand}, from your collection`;
  if (item.material) return `${item.material}, from your collection`;
  if (archetypePrimary) return `Part of your ${archetypePrimary} signature`;
  return null;
}

function JewelryGridCard({
  item: j,
  showFavorite,
  onToggleFav,
  onEdit,
  onDelete,
}: {
  item: JewelryItem;
  showFavorite: boolean;
  onToggleFav: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const reveal = useCardReveal();
  const { data: profile } = useProfile();
  const hasId = !!j.id;
  return (
    <div ref={reveal.rootRef} {...reveal.bind} className="group relative" style={{ border: "1px solid var(--caileve-gold-12)", boxShadow: "var(--caileve-shadow-card)", borderRadius: "var(--caileve-radius-md)" }}>
      {hasId ? (
        <Link
          to="/jewelry/$id"
          params={{ id: j.id }}
          className="block cursor-pointer"
          aria-label={`View ${j.name} details`}
        >
          <ImageBox
            src={j.prefer_clean_bg && j.photo_url_no_bg ? j.photo_url_no_bg : j.photo_url}
            alt={j.name}
            style={j.prefer_clean_bg && j.photo_url_no_bg ? { backgroundColor: bgColorFor(j.clean_bg_color) } : undefined}
          />
          <div className="mt-3 pb-3 px-1">
            <p className="font-serif-display text-xl leading-tight line-clamp-2">{j.name}</p>
            {j.category && (
              <p className="uppercase mt-1" style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--caileve-muted)" }}>
                {j.category}
              </p>
            )}
            {(() => {
              const line = compositionLine(j, profile?.style_archetype_primary ?? null);
              return line ? (
                <p className="mt-1.5" style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--caileve-muted)", fontStyle: "italic" }}>
                  {line}
                </p>
              ) : null;
            })()}
          </div>
        </Link>
      ) : (
        <button
          type="button"
          onClick={() => toast.error("This item is missing an ID and can't be opened.")}
          className="block w-full text-left cursor-pointer"
        >
          <ImageBox
            src={j.prefer_clean_bg && j.photo_url_no_bg ? j.photo_url_no_bg : j.photo_url}
            alt={j.name}
            style={j.prefer_clean_bg && j.photo_url_no_bg ? { backgroundColor: bgColorFor(j.clean_bg_color) } : undefined}
          />
          <div className="mt-3 pb-3 px-1">
            <p className="font-serif-display text-xl leading-tight line-clamp-2">{j.name}</p>
            {j.category && (
              <p className="uppercase mt-1" style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--caileve-muted)" }}>
                {j.category}
              </p>
            )}
            {(() => {
              const line = compositionLine(j, profile?.style_archetype_primary ?? null);
              return line ? (
                <p className="mt-1.5" style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--caileve-muted)", fontStyle: "italic" }}>
                  {line}
                </p>
              ) : null;
            })()}
          </div>
        </button>
      )}
      <div className="absolute top-2.5 right-2.5 z-10">
        <FavoriteHeart active={!!j.is_favorite} onToggle={onToggleFav} />
        
      </div>
      <CardRevealOverlay revealed={reveal.revealed} onEdit={onEdit} onDelete={onDelete} />
    </div>
  );
}

function CollectionSkeletonGrid({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-6">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i}>
          <div
            className="animate-pulse w-full"
            style={{ aspectRatio: "1 / 1", backgroundColor: "var(--caileve-soft)" }}
          />
          <div
            className="animate-pulse mt-3"
            style={{ height: 16, width: "70%", backgroundColor: "var(--caileve-soft)" }}
          />
          <div
            className="animate-pulse mt-2"
            style={{ height: 10, width: "40%", backgroundColor: "var(--caileve-soft)" }}
          />
        </div>
      ))}
    </div>
  );
}